import { Suspense, lazy } from 'react';

import { useNavigate } from 'react-router';

import { showConfirm } from '../ui/confirm-dialog';

const ArrowLeft = lazy(() =>
  import('lucide-react').then(mod => ({ default: mod.ArrowLeft })),
);

interface SolveTopBarProps {
  title: string;
  courseId: string | undefined;
  remainingSubmissions: number;
  maxSubmissions: number;
  /** 현재까지 채점된 총점(제출 이력 없으면 null). UX-04 */
  submitScore: number | null;
  /** 과제 만점(문항 배점 합). UX-04 */
  totalScore: number;
}

const SolveTopBar = ({
  title,
  courseId,
  remainingSubmissions,
  maxSubmissions,
  submitScore,
  totalScore,
}: SolveTopBarProps) => {
  const roundedScore =
    submitScore == null ? null : Math.round(submitScore * 10) / 10;
  const navigate = useNavigate();

  const handleExit = async () => {
    const result = await showConfirm({
      title: '과제 나가기',
      description:
        '제출하지 않은 답안은 저장되지 않을 수 있습니다. 정말 나가시겠습니까?',
      variant: 'destructive',
      confirmText: '나가기',
      cancelText: '계속 풀기',
    });
    if (!result) return;
    if (courseId) {
      navigate(`/course/${courseId}`);
    } else {
      navigate('/course');
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between h-12 px-3 md:px-6 gap-3">
        <button
          type="button"
          onClick={handleExit}
          className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 font-medium shrink-0"
        >
          <Suspense fallback={<div className="h-4 w-4" />}>
            <ArrowLeft className="h-4 w-4" />
          </Suspense>
          <span>나가기</span>
        </button>

        <h1 className="text-sm md:text-base font-bold text-gray-900 truncate flex-1 text-center">
          {title}
        </h1>

        <div className="shrink-0 flex items-center gap-2 md:gap-3 text-xs md:text-sm">
          {roundedScore != null && (
            <div className="flex items-center gap-1" aria-label="현재 점수">
              <span className="text-gray-500 hidden sm:inline">점수</span>
              <span className="font-bold text-gray-900">{roundedScore}</span>
              <span className="text-gray-400">/ {totalScore}</span>
            </div>
          )}
          <span className="text-gray-500 hidden sm:inline">이 문제 남은 제출</span>
          <span className="text-gray-500 sm:hidden">남은</span>
          <span
            className={`font-bold ${
              remainingSubmissions > 0 ? 'text-orange-500' : 'text-destructive'
            }`}
          >
            {remainingSubmissions}
          </span>
          {/* 교수가 제출횟수를 추가하면 remaining이 max를 넘을 수 있어, 분모는 remaining 이상으로 표시(분자>분모 혼동 방지) */}
          <span className="text-gray-400">/ {Math.max(remainingSubmissions, maxSubmissions)}</span>
        </div>
      </div>
    </header>
  );
};

export default SolveTopBar;
